import { observable, computed, action, makeObservable } from 'mobx';
import moment from 'moment';
import {t} from 'shared/core';
import _ from 'lodash';

class DayOffViewModel {
  json;
  unit;
  editable;
  showErrorMessage = false;

  constructor(json, editable=true) {
    makeObservable(this, {
      json: observable,
      unit: observable,
      editable: observable,
      showErrorMessage: observable,
      updateUnit: action,
      updateStartTime: action,
      updateEndTime: action,
      updateHours: action,
      days: computed,
      hoursExact: computed,
      hours: computed,
      hasHoliday: computed,
      hasFullDayHoliday: computed,
      hasHalfDayHoliday: computed,
      workDayLengthHours: computed,
      holidayName: computed,
      hasCompanyDayOff: computed,
      date: computed,
      canUpdateHours: computed,
      clickable: computed,
      timeRange: computed,
      errorMessage: computed
    });

    this.json = json;
    this.unit = this.guessUnit();
    this.editable = editable;
  }

  equalTo(other) {
    return this.json.date === other.json.date;
  }

  guessUnit() {
    if (this.json.work_day_length_in_seconds === Math.round(this.json.cost * 3600)) return 'full';
    if (this.json.work_day_length_in_seconds === Math.round(this.json.cost * 3600 * 2)) return 'half';
    return 'custom';
  }

  updateUnit(unit) {
    this.showErrorMessage = false;
    this.unit = unit;

    switch (unit) {
      case 'full':
        if (this.json.start_time) {
          this.json.end_time = moment(moment(this.json.start_time, ['h:m a', 'H:m']) + (this.json.work_day_length_in_seconds * 1000)).format(t('components.dates.TIMESTAMP_FORMAT'));
        }
        break;
      case 'half':
        if (this.json.start_time) {
          this.json.end_time = moment(moment(this.json.start_time, ['h:m a', 'H:m']) + (this.json.work_day_length_in_seconds / 2 * 1000)).format(t('components.dates.TIMESTAMP_FORMAT'));
        }
        break;
      case 'custom':
      default:
        break;
    }
  }

  updateStartTime(value) {
    this.showErrorMessage = false;
    this.json.start_time = value;
    if (this.unit === 'half' && this.json.start_time) {
      this.json.end_time = moment(moment(this.json.start_time, ['h:m a', 'H:m']) + (this.json.work_day_length_in_seconds / 2 * 1000)).format(t('components.dates.TIMESTAMP_FORMAT'));
    }
  }

  updateEndTime(value) {
    this.showErrorMessage = false;
    this.json.end_time = value;
    if (this.unit === 'half') {
      this.json.start_time = moment(moment(this.json.end_time, ['h:m a', 'H:m']) - (this.json.work_day_length_in_seconds / 2 * 1000)).format(t('components.dates.TIMESTAMP_FORMAT'));
    }
  }

  updateHours(cost, cost_in_seconds, start_time, end_time) {
    this.json.cost = cost;
    this.json.cost_in_seconds = cost_in_seconds;
    this.json.start_time = start_time;
    this.json.end_time = end_time;
  }

  get days() {
    if (this.hasFullDayHoliday || this.hasCompanyDayOff) {
      return 0;
    }

    if (this.unit === 'full' && !this.json.start_time && !this.json.end_time) {
      return 1;
    } if (this.unit === 'half' && this.hasHalfDayHoliday && !this.json.start_time && !this.json.end_time) {
      return 0.5;
    }

    if (!this.json.start_time && !this.json.end_time) {
      return Math.round((100.0 * this.json.cost_in_seconds) / this.json.work_day_length_in_seconds) / 100;
    }

    if (!this.json.start_time || !this.json.end_time) {
      return null;
    }

    let start_time = moment(this.json.start_time, ['h:m a', 'H:m']);
    let end_time = moment(this.json.end_time, ['h:m a', 'H:m']);

    return Math.round((end_time - start_time) / this.json.work_day_length_in_seconds / 10) / 100;
  }

  get hoursExact() {
    if (this.hasFullDayHoliday || this.hasCompanyDayOff) {
      return 0;
    }

    if (this.unit === 'full' && !this.json.start_time && !this.json.end_time) {
      return this.json.work_day_length_in_seconds / 3600;
    } if (this.unit === 'half' && this.hasHalfDayHoliday && !this.json.start_time && !this.json.end_time) {
      return this.json.work_day_length_in_seconds / 3600 / 2;
    }

    if (!this.json.start_time && !this.json.end_time) {
      return Math.round((100.0 * this.json.cost_in_seconds) / 3600) / 100;
    }

    if (!this.json.start_time || !this.json.end_time) {
      return null;
    }

    let start_time = moment(this.json.start_time, ['h:m a', 'H:m']);
    let end_time = moment(this.json.end_time, ['h:m a', 'H:m']);

    return (end_time - start_time) / 3600000;
  }

  get hours() {
    if (_.isNull(this.hoursExact)) {
      return null;
    }

    return Math.round(this.hoursExact * 100) / 100;
  }

  get hasHoliday() {
    return !!this.json.holiday;
  }

  get hasFullDayHoliday() {
    return this.json.holiday_duration === 'full_day';
  }

  get hasHalfDayHoliday() {
    return this.json.holiday_duration === 'half_day';
  }

  get workDayLengthHours() {
    return this.json.work_day_length_in_seconds / 3600;
  }

  get holidayName() {
    return this.json.holiday;
  }

  get hasCompanyDayOff() {
    return this.json.day_off;
  }

  get date() {
    return new Date(moment(this.json.date));
  }

  get canUpdateHours() {
    return !this.hasCompanyDayOff && !this.hasFullDayHoliday && this.editable;
  }

  get clickable() {
    return this.canUpdateHours;
  }

  get timeRange() {
    if (!this.json.start_time) {
      return null;
    }

    return `${moment(this.json.start_time, ['h:m a', 'H:m']).format(t('components.dates.TIMESTAMP_FORMAT'))} - ${moment(this.json.end_time, ['h:m a', 'H:m']).format(t('components.dates.TIMESTAMP_FORMAT'))}`;
  }

  get errorMessage() {
    if (!(this.unit === 'full') && (!this.json.start_time || !this.json.end_time)) {
      return t('employees.profile.time_off.request.Times are required');
    } else if (Math.round(this.hoursExact * 3600) > this.json.work_day_length_in_seconds) {
      return t('employees.profile.time_off.request.Absence cannot be longer than one day');
    } else if (this.hasHalfDayHoliday && Math.round(this.hoursExact * 3600 * 2) > this.json.work_day_length_in_seconds) {
      return t('employees.profile.time_off.request.Absence cannot be longer than half a day');
    } else if (this.hoursExact < 0){
      return t('employees.profile.time_off.request.End time cannot be before start time');
    }

    return null;
  }
}

export default DayOffViewModel;
